import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import { Button } from "@/components/primitives/button";
import {
  READD_HINT_SEEN_KEY,
  READD_HINT_SNOOZE_KEY,
  useAnnouncementSlot,
} from "@/zustand/AnnouncementState";
import { safeStorage } from "@/utils/safeStorage";

const SNOOZE_DAYS = 7;

/**
 * Shown only inside a LEGACY iOS home-screen install (opaque status bar
 * baked into the webclip — see AnnouncementState.isLegacyWebclip). iOS has
 * no way to refresh a webclip's icon or status-bar style in place, so the
 * only path to the full Midnight Emerald look is re-adding the app. This
 * is purely cosmetic, so the hint is gentle: one dialog, snoozeable, and
 * "don't show again" is a single tap.
 */
export default function ReAddHomeScreenHint() {
  const canShow = useAnnouncementSlot("readd-hint");
  const [open, setOpen] = useState(true);

  if (!canShow || !open) {
    return null;
  }

  const snooze = () => {
    safeStorage.set(
      READD_HINT_SNOOZE_KEY,
      String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000),
    );
    setOpen(false);
  };

  const dismissForever = () => {
    safeStorage.set(READD_HINT_SEEN_KEY, "true");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && snooze()}>
      <DialogContent className="max-w-md border-primary/40">
        <DialogHeader>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-[radial-gradient(circle_at_top,_hsl(var(--primary)/0.35),_transparent_70%)]" />
          <DialogTitle className="pt-1 text-center text-lg">
            One last touch for the new look
          </DialogTitle>
        </DialogHeader>

        <DialogDescription asChild>
          <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
            <p>
              Your home-screen icon was added before the Midnight Emerald
              update. iOS keeps the old icon and the black bar behind the clock
              until the app is added again — everything else is already up to
              date.
            </p>
            <ol className="list-decimal space-y-1.5 pl-5">
              <li>
                Press and hold the ORION icon, then choose{" "}
                <span className="font-medium text-foreground">
                  Delete Bookmark
                </span>{" "}
                — your messages are safe.
              </li>
              <li>
                Open{" "}
                <span className="font-medium text-foreground">
                  {window.location.host}
                </span>{" "}
                in Safari.
              </li>
              <li>
                Tap <span className="font-medium text-foreground">Share</span> →{" "}
                <span className="font-medium text-foreground">
                  Add to Home Screen
                </span>
                , then sign in once.
              </li>
            </ol>
          </div>
        </DialogDescription>

        <div className="mt-1 flex flex-col gap-2">
          <Button onClick={dismissForever}>Got it</Button>
          <Button variant="outline" onClick={snooze}>
            Remind me later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
